// Single source of truth for SEO metadata — consumed both at runtime (the root
// layout updates the document head on navigation) and at build time (the
// prerender script in scripts/prerender.ts writes a static index.html per route
// with these tags baked in, so non-JS crawlers and social scrapers get correct
// per-page meta).

import type { BlogPost } from './blogPosts'
import { formatAuthors } from './blogPosts'
import { PRESS_RELEASES, type PressRelease } from './pressReleases'

export const SITE = {
  baseUrl: 'https://ent.ai',
  name: 'Ent',
  ogImage: 'https://ent.ai/images/og-preview.png',
  /** Square brand mark for structured-data `logo` (the OG banner is 1200×627). */
  logo: 'https://ent.ai/android-chrome-512x512-light.png',
} as const

/**
 * Absolute canonical URL for a site path. The hosting layer 301s every
 * directory route to its trailing-slash form (/platform → /platform/) and only
 * that form answers 200, so canonicals, og:url, the sitemap and JSON-LD all use
 * it — pointing them at the bare path would name a redirect as the canonical.
 */
export function canonicalUrl(path: string): string {
  const trimmed = path.replace(/\/+$/, '')
  return `${SITE.baseUrl}${trimmed}/`
}

export type RouteMeta = {
  title: string
  description: string
  /** Absolute OG/Twitter image URL for this route; falls back to SITE.ogImage. */
  image?: string
}

export const BLOG_TITLE_SUFFIX = ' — Ent Blog'

export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'Ent — Intent-Aware Security for the Enterprise',
    description:
      'Ent is the intent-aware AI endpoint — reading human, AI, and app activity in real time to tell normal work from risky action and act at the moment of risk.',
  },
  '/platform': {
    title: 'Platform — Ent | Intent-Aware Endpoint Security',
    description:
      'Explore the Ent platform — intent-aware endpoint security that reads user and agent activity in real time to observe, investigate, and intervene.',
  },
  '/company': {
    title: 'About Ent — Team, Investors & Advisors | Intent-Aware Security',
    description:
      'Meet the team behind Ent — threat researchers, security practitioners, and engineers, backed by Craft, Decibel, Sequoia, and Shield Capital.',
  },
  '/blog': {
    title: 'Blog — Ent | Intent-Aware Security Insights',
    description:
      'Research, engineering, and product writing from the Ent team — on intent-aware security, detection engineering, AI usage, and more.',
  },
  '/press': {
    title: 'Press Release — Ent | Intent-Aware Endpoint Security',
    description:
      'Ent appoints Kevin Arsenault, former CrowdStrike and Proofpoint sales executive, as chief revenue officer to scale revenue and expand enterprise adoption.',
  },
  '/news': {
    title: 'News — Ent | Intent-Aware Security in the Press',
    description:
      'Ent in the news — coverage, interviews, and podcasts on intent-aware security, AI-driven threat detection, and behavioral endpoint protection.',
  },
  '/contact': {
    title: 'Schedule a Demo — Ent | See Intent-Aware Security in Action',
    description:
      'Book a live demo to see Ent\'s intent-aware AI endpoint in action — reading user and AI activity in real time to act at the moment of risk.',
  },
  '/joinus': {
    title: 'Careers at Ent — Help Shape the Future of Security',
    description:
      'Ent is hiring world-class engineers, researchers, and security practitioners. Share your details to help shape the future of security.',
  },
  '/legal': {
    title: 'Legal Documents — Ent Security',
    description:
      'Access Ent legal documents including our privacy policy, terms of use, subscription terms, data processing addendum, and sub-processor list.',
  },
  '/privacy': {
    title: 'Privacy Policy — Ent Security',
    description:
      'Read how Ent collects, uses, and protects your personal information — covering data handling, retention, and your rights under applicable law.',
  },
  '/terms': {
    title: 'Terms of Use — Ent Security',
    description:
      'Terms of use governing your access to ent.ai and the Ent security platform. Read our conditions for website usage and service agreements.',
  },
  '/subscription': {
    title: 'Subscription Terms — Ent Security Platform',
    description:
      'Ent subscription terms and conditions for the intent-aware security platform. Covers licensing, support, and service level commitments.',
  },
  '/sub-processors': {
    title: 'Sub-Processors — Ent Security',
    description:
      'View the list of sub-processors Ent engages to deliver our intent-aware security platform, including their roles and data processing locations.',
  },
  '/dpa': {
    title: 'Data Processing Addendum — Ent Security',
    description:
      'Ent data processing addendum covering GDPR and privacy compliance, data handling practices, security measures, and controller-processor obligations.',
  },
  '/partners': {
    title: 'Partner Deal Registration — Ent',
    description:
      'Register a partner deal with Ent — submit customer and partner details for resellers, MSSPs, systems integrators, and technology partners.',
  },
}

/**
 * Unlisted routes: real pages, reachable only by someone who has the direct
 * link. They carry no nav or footer entry, are excluded from sitemap.xml, and
 * are served `noindex, nofollow` (both in the prerendered HTML and after
 * hydration) so they stay out of search results.
 *
 * Distinct from the vanity redirects in scripts/prerender.ts, which have no
 * content of their own. These still need ROUTE_META — the prerender and the
 * runtime head updater both read it — so the exclusions live here rather than
 * being expressed by absence.
 */
export const UNLISTED_ROUTES: ReadonlySet<string> = new Set(['/partners'])

export const isUnlistedRoute = (route: string) => UNLISTED_ROUTES.has(route)

/** Routes prerendered to static HTML at build time (all known static routes). */
export const PRERENDER_ROUTES = Object.keys(ROUTE_META)

// ── Blog post SEO ────────────────────────────────────────────────────────────

/**
 * Resolve a blog post's cover image to an absolute URL suitable for og:image.
 * HubSpot featured/inline images arrive as absolute CDN URLs (used as-is);
 * curated fallback covers are bare filenames under /images/blog/.
 */
export function blogImageUrl(image: string | undefined): string {
  if (!image) return SITE.ogImage
  if (/^https?:\/\//.test(image)) return image
  if (image.startsWith('//')) return `https:${image}`
  return `${SITE.baseUrl}/images/blog/${image}`
}

export type ResolvedBlogMeta = {
  title: string
  description: string
  url: string
  image: string
  /** Display byline ("A, B & C"), for the flat <meta name="author"> tag. */
  author: string
  /** Individual author names, for structured data that lists authors separately. */
  authorNames: string[]
  publishedTime?: string
  modifiedTime?: string
}

/**
 * Per-post SEO metadata, preferring HubSpot's editor-controlled SEO fields
 * (seoTitle / metaDescription) and falling back to the post title / excerpt.
 * Shared by the build-time prerender and the runtime route head updater.
 */
export function blogPostMeta(post: BlogPost): ResolvedBlogMeta {
  const title = post.seoTitle?.trim() || `${post.title}${BLOG_TITLE_SUFFIX}`
  const description =
    post.metaDescription?.trim() ||
    post.excerpt?.trim() ||
    `${post.title} — from the Ent blog.`
  return {
    title,
    description,
    url: canonicalUrl(`/blog/${post.slug}`),
    image: blogImageUrl(post.image),
    author: formatAuthors(post.authors),
    authorNames: post.authors.map((a) => a.name).filter(Boolean),
    publishedTime: post.date,
    modifiedTime: post.updated || post.date,
  }
}

/**
 * Runtime: write resolved blog-post meta into the document head on client-side
 * navigation (the prerendered HTML already carries it on first load). Mirrors
 * the static-route updater in the root layout, plus the article-specific tags.
 */
export function applyBlogMeta(meta: ResolvedBlogMeta): void {
  const set = (selector: string, attr: string, value: string) => {
    document.querySelector(selector)?.setAttribute(attr, value)
  }
  document.title = meta.title
  set('meta[name="description"]', 'content', meta.description)
  set('meta[name="author"]', 'content', meta.author)
  set('meta[property="og:type"]', 'content', 'article')
  set('meta[property="og:title"]', 'content', meta.title)
  set('meta[property="og:description"]', 'content', meta.description)
  set('meta[property="og:url"]', 'content', meta.url)
  set('meta[property="og:image"]', 'content', meta.image)
  set('meta[property="og:image:alt"]', 'content', meta.title)
  // The base shell's og:image dimensions/type describe the default OG banner,
  // not this post's cover (HubSpot CDN covers are arbitrary size). Drop them so
  // scrapers re-fetching after hydration don't read stale dimensions — mirrors
  // the build-time prerender, which strips these tags for blog posts.
  document.querySelector('meta[property="og:image:width"]')?.remove()
  document.querySelector('meta[property="og:image:height"]')?.remove()
  document.querySelector('meta[property="og:image:type"]')?.remove()
  set('meta[name="twitter:title"]', 'content', meta.title)
  set('meta[name="twitter:description"]', 'content', meta.description)
  set('meta[name="twitter:image"]', 'content', meta.image)
  set('link[rel="canonical"]', 'href', meta.url)
  set('link[rel="alternate"][hreflang]', 'href', meta.url)
}

// ── Press release SEO ──────────────────────────────────────────────────────────

export type ResolvedPressMeta = {
  title: string
  description: string
  url: string
  /** Publish date, ISO 8601 — for article:published_time. */
  publishedTime: string
}

/** The evergreen /press landing always shows the latest release; its canonical
 *  points at that release's stable permalink so the permanent per-release URL —
 *  not /press — is the one search engines index. */
export function pressIndexCanonicalUrl(): string {
  const latest = PRESS_RELEASES[0]
  return canonicalUrl(latest ? `/press/${latest.slug}` : '/press')
}

/** Per-release SEO metadata for the /press/$slug permalink. Every release —
 *  including the latest — has one, and it is that release's canonical URL. */
export function pressReleaseMeta(release: PressRelease): ResolvedPressMeta {
  return {
    title: `${release.headline} — Ent`,
    description: (release.subhead || release.headline).trim(),
    url: canonicalUrl(`/press/${release.slug}`),
    publishedTime: release.date,
  }
}

/**
 * Runtime: write resolved press-release meta into the document head on
 * client-side navigation (the prerendered HTML already carries it on first
 * load). Press releases keep the default OG banner, so only the text/URL tags
 * and og:type change — no image swap.
 */
export function applyPressMeta(meta: ResolvedPressMeta): void {
  const set = (selector: string, attr: string, value: string) => {
    document.querySelector(selector)?.setAttribute(attr, value)
  }
  document.title = meta.title
  set('meta[name="description"]', 'content', meta.description)
  set('meta[property="og:type"]', 'content', 'article')
  set('meta[property="og:title"]', 'content', meta.title)
  set('meta[property="og:description"]', 'content', meta.description)
  set('meta[property="og:url"]', 'content', meta.url)
  set('meta[property="og:image:alt"]', 'content', meta.title)
  set('meta[name="twitter:title"]', 'content', meta.title)
  set('meta[name="twitter:description"]', 'content', meta.description)
  set('link[rel="canonical"]', 'href', meta.url)
  set('link[rel="alternate"][hreflang]', 'href', meta.url)
}
